import { useEffect, useCallback, useState, useLayoutEffect } from 'react';
import type { RefObject } from 'react';

export const useListBoxPosition = ({
  isListBoxOpen,
  buttonRef,
  listBoxRef,
  padding,
}: {
  isListBoxOpen: boolean;
  buttonRef: RefObject<HTMLDivElement>;
  listBoxRef: RefObject<HTMLDivElement>;
  padding: number;
}): 'above' | 'below' => {
  const [position, setPosition] = useState<'above' | 'below'>('below');

  const updatePosition = useCallback(() => {
    const listBoxRect = listBoxRef.current?.getBoundingClientRect();
    const buttonRect = buttonRef.current?.getBoundingClientRect();
    if (listBoxRect === undefined || buttonRect === undefined || !isListBoxOpen) return;

    const lowestPoint = buttonRect.bottom + padding + listBoxRect.height;
    const highestPoint = buttonRect.top - (listBoxRect.height + padding);

    setPosition(
      lowestPoint < (window.innerHeight ?? 0) ||
        (highestPoint < 0 && highestPoint < window.innerHeight - lowestPoint)
        ? 'below'
        : 'above',
    );
  }, [buttonRef, isListBoxOpen, listBoxRef, padding]);

  useEffect(() => {
    const button = buttonRef.current;
    const listBox = listBoxRef.current;
    if (button === null || listBox === null || !isListBoxOpen) return;
    const listBoxRect = listBox.getBoundingClientRect();
    const intersectionObserver = new IntersectionObserver(
      () => {
        updatePosition();
      },
      {
        root: null,
        rootMargin: `0px 0px -${listBoxRect.height + padding}px 0px`,
        threshold: [0, 1],
      },
    );
    intersectionObserver.observe(button);

    return () => {
      intersectionObserver.unobserve(button);
    };
  }, [position, isListBoxOpen, buttonRef, listBoxRef, padding, updatePosition]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  return position;
};
